'use client';

import Link from 'next/link';
import Image from 'next/image';
import Header from '@/components/common/Header';
import Icon from '@/components/ui/AppIcon';

interface CaseStudyPageProps {
  title: string;
  subtitle: string;
  sector: string;
  location: string;
  client: string;
  heroImage: string;
  heroImageAlt: string;
  challenges: string[];
  diaaxResponse: string[];
  impact: string[];
  ctaPrompt?: string;
}

export default function CaseStudyPage({
  title,
  subtitle,
  sector,
  location,
  client,
  heroImage,
  heroImageAlt,
  challenges,
  diaaxResponse,
  impact,
  ctaPrompt = 'Ready to transform your institution?',
}: CaseStudyPageProps) {
  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero Banner */}
      <section className="bg-institutional py-10 sm:py-14 lg:py-24">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <Link
              href="/case-studies"
              className="inline-flex items-center text-white/70 hover:text-white text-sm mb-6 sm:mb-8 transition-fast"
            >
              <Icon name="ArrowLeftIcon" size={16} className="mr-2" />
              Back to Case Studies
            </Link>

            <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
              <span className="inline-block px-3 py-1 bg-white/15 text-white text-xs font-semibold rounded-full tracking-wide">
                {sector}
              </span>
              <span className="flex items-center gap-1 text-white/70 text-sm">
                <Icon name="MapPinIcon" size={14} className="text-white/70" />
                {location}
              </span>
            </div>

            <h1 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-white leading-tight tracking-tight mb-4 sm:mb-6">
              {title}
            </h1>

            <p className="text-white/80 text-base sm:text-lg leading-relaxed max-w-3xl">
              {subtitle}
            </p>

            <div className="mt-6 sm:mt-8 pt-6 sm:pt-8 border-t border-white/20">
              <p className="text-white/60 text-xs sm:text-sm">
                <span className="font-medium text-white/80">Client:</span>{' '}
                {client} &nbsp;|&nbsp;
                <span className="font-medium text-white/80">Location:</span>{' '}
                {location}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Image - responsive height */}
      <div className="w-full relative overflow-hidden" style={{ aspectRatio: '16/7' }}>
        <Image
          src={heroImage}
          alt={heroImageAlt}
          fill
          className="object-cover"
          priority
          sizes="100vw"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background/50 via-transparent to-transparent" />
      </div>

      {/* Content */}
      <section className="py-10 sm:py-14 lg:py-24 bg-background">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-5xl mx-auto">

            {/* Three Columns - stacked on mobile, grid on desktop */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 sm:gap-6 lg:gap-10">

              {/* Challenge */}
              <div className="bg-red-50/60 rounded-xl p-5 sm:p-6 lg:p-8 border border-red-100">
                <div className="flex items-center gap-3 mb-4 sm:mb-6">
                  <div className="flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 bg-red-100 rounded-lg flex-shrink-0">
                    <Icon name="ExclamationTriangleIcon" size={18} className="text-red-600 sm:hidden" />
                    <Icon name="ExclamationTriangleIcon" size={20} className="text-red-600 hidden sm:block" />
                  </div>
                  <h2 className="text-base sm:text-lg font-bold text-red-700 tracking-tight">Challenge</h2>
                </div>
                <ul className="space-y-3 sm:space-y-4">
                  {challenges.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="mt-2 w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" />
                      <p className="text-slate-700 text-sm leading-relaxed">{item}</p>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Diaax Response */}
              <div className="bg-institutional/5 rounded-xl p-5 sm:p-6 lg:p-8 border border-institutional/15">
                <div className="flex items-center gap-3 mb-4 sm:mb-6">
                  <div className="flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 bg-institutional/10 rounded-lg flex-shrink-0">
                    <Icon name="LightBulbIcon" size={18} className="text-institutional sm:hidden" />
                    <Icon name="LightBulbIcon" size={20} className="text-institutional hidden sm:block" />
                  </div>
                  <h2 className="text-base sm:text-lg font-bold text-institutional tracking-tight">Diaax Response</h2>
                </div>
                <ul className="space-y-3 sm:space-y-4">
                  {diaaxResponse.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="mt-2 w-1.5 h-1.5 rounded-full bg-institutional flex-shrink-0" />
                      <p className="text-slate-700 text-sm leading-relaxed">{item}</p>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Impact */}
              <div className="bg-teal-50/60 rounded-xl p-5 sm:p-6 lg:p-8 border border-teal-100">
                <div className="flex items-center gap-3 mb-4 sm:mb-6">
                  <div className="flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 bg-teal-100 rounded-lg flex-shrink-0">
                    <Icon name="CheckCircleIcon" size={18} className="text-teal-600 sm:hidden" />
                    <Icon name="CheckCircleIcon" size={20} className="text-teal-600 hidden sm:block" />
                  </div>
                  <h2 className="text-base sm:text-lg font-bold text-teal-700 tracking-tight">Impact</h2>
                </div>
                <ul className="space-y-3 sm:space-y-4">
                  {impact.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="mt-2 w-1.5 h-1.5 rounded-full bg-teal-500 flex-shrink-0" />
                      <p className="text-slate-700 text-sm leading-relaxed">{item}</p>
                    </li>
                  ))}
                </ul>
              </div>

            </div>

            {/* CTA */}
            <div className="mt-10 sm:mt-14 lg:mt-16 pt-8 sm:pt-10 lg:pt-12 border-t border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-6">
              <div>
                <p className="text-text-secondary text-sm">{ctaPrompt}</p>
                <p className="text-institutional font-semibold mt-1">Let&apos;s discuss your strategic challenges.</p>
              </div>
              <Link
                href="/contact"
                className="inline-block w-full sm:w-auto px-6 sm:px-8 py-3 bg-institutional text-institutional-foreground font-semibold rounded-md hover:bg-institutional/90 transition-fast institutional-shadow text-center whitespace-nowrap"
              >
                Request a Conversation
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
